import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Button from "@mui/material/Button";
import { toast } from "react-toastify";
import GenericModal from "../GenericComponents/GenericModal";
import CustomDeleteFontsModal from "./CustomDeleteFontsModal";
import UploadedFontsList from "./UploadedFontsList";
import {
  CUSTOM_FONT_SUPPORTED_FORMATS_LABEL,
  getCustomFontFileNameWithExtension,
  getCustomFontUnsupportedFormatMessage,
  isCustomFontFileSupported,
} from "../../Uitls/customFontsStorage";
import {
  hasUnresolvedCustomFontDuplicate,
  isDuplicateFontFlag,
  uploadCustomFontsToApi,
} from "../../services/customFontsApi";
import {
  buildCustomFontsModalBodyStyle,
  resolveCustomFontsModalLayout,
} from "./customFontsModalLayout";
import "./CustomFontsUploadModal.scss";
import UploadIcon from "../../images/uploadIcon.svg";

export { CUSTOM_FONTS_MODAL_LAYOUT } from "./customFontsModalLayout";

const MAX_FONT_FILE_BYTES = 20 * 1024 * 1024;

export default function CustomFontsUploadModal({
  open,
  onClose,
  assetInfo,
  savedCustomFontRecords,
  onFontsListUpdated,
  onDeleteSavedFontRecord,
  layout: layoutOverrides,
}) {
  const layout = useMemo(
    () => resolveCustomFontsModalLayout(layoutOverrides),
    [layoutOverrides],
  );
  const modalBodyStyle = useMemo(() => buildCustomFontsModalBodyStyle(), []);

  const hiddenFileInputRef = useRef(null);
  const replaceFileInputRef = useRef(null);
  const pendingReplaceRecordRef = useRef(null);

  const [isUploading, setIsUploading] = useState(false);
  const [pendingUploads, setPendingUploads] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeletingFont, setIsDeletingFont] = useState(false);
  const [fontIdPendingDelete, setFontIdPendingDelete] = useState(null);
  const [fontLabelPendingDelete, setFontLabelPendingDelete] = useState("");

  const hasUnresolvedDuplicate = useMemo(
    () => hasUnresolvedCustomFontDuplicate(savedCustomFontRecords),
    [savedCustomFontRecords],
  );

  useEffect(() => {
    if (!open) {
      setIsDeleteConfirmOpen(false);
      setIsDeletingFont(false);
      setFontIdPendingDelete(null);
      setFontLabelPendingDelete("");
      pendingReplaceRecordRef.current = null;
      setPendingUploads([]);
    }
  }, [open]);

  const updatePendingProgress = useCallback(
    (pendingIds, progress, progressText) => {
      setPendingUploads((prev) =>
        prev.map((row) =>
          pendingIds.includes(row.id)
            ? { ...row, progress, progressText }
            : row,
        ),
      );
    },
    [],
  );

  const refreshFontsList = useCallback(async () => {
    if (onFontsListUpdated) {
      await onFontsListUpdated();
    }
  }, [onFontsListUpdated]);

  const getProgressText = (progress) => {
    if (progress <= 10) return `${progress}% uploading...`;
    return `${progress}% Processing font...`;
  };

  const saveFontFilesImmediately = useCallback(
    async (fileList, options = {}) => {
      const files = Array.from(fileList || []);
      if (files.length === 0) return false;

      const validFiles = [];
      for (const file of files) {
        if (!isCustomFontFileSupported(file)) {
          toast.error(
            <div className="notificationText">
              {getCustomFontUnsupportedFormatMessage(file.name)}
            </div>,
          );
          continue;
        }
        if (file.size > MAX_FONT_FILE_BYTES) {
          toast.error(
            <div className="notificationText">
              Skipped {file.name}: max file size is 20 MB.
            </div>,
          );
          continue;
        }
        validFiles.push(file);
      }

      if (validFiles.length === 0) return false;

      const newPendingRows = validFiles.map((file, index) => ({
        id: `pending-${Date.now()}-${index}`,
        label: file.name,
        progress: 0,
        progressText: "0% uploading...",
        isPotentialDuplicate: savedCustomFontRecords.some(
          (r) => r.label === file.name.replace(/\.(ttf|otf)$/i, "")
        ),
      }));
      
      const pendingIds = newPendingRows.map((row) => row.id);

      setPendingUploads((prev) => [...prev, ...newPendingRows]);
      setIsUploading(true);

      const responses = await Promise.all(
        validFiles.map((file, index) =>
          uploadFile(file, pendingIds[index], options),
        ),
      );

      const uploadSucceeded = responses.every(Boolean);
      setIsUploading(false);
      return uploadSucceeded;
    },
    [
      assetInfo?.assetId,
      assetInfo?.assetName,
      refreshFontsList,
      updatePendingProgress,
      savedCustomFontRecords
    ],
  );

  const handleHiddenInputChange = async (event) => {
    await saveFontFilesImmediately(event.target.files);
    event.target.value = "";
  };

  const handleReplaceInputChange = async (event) => {
    const file = event.target.files?.[0];
    const record = pendingReplaceRecordRef.current;
    event.target.value = "";
    pendingReplaceRecordRef.current = null;

    if (!file || !record) return;

    if (record.isFailedUpload) {
      setPendingUploads((prev) =>
        prev.filter((item) => item.id !== record.pendingId),
      );

      await saveFontFilesImmediately([file]);
      return;
    }

    if (!record.duplicateOfFontId) return;

    await saveFontFilesImmediately([file], {
      replaceFontId: record.id,
    });
  };

  const handleReplaceRequest = (record) => {
    if (isUploading || !isDuplicateFontFlag(record)) return;
    pendingReplaceRecordRef.current = record;
    replaceFileInputRef.current?.click();
  };

  const openFilePicker = () => {
    if (!isUploading) {
      hiddenFileInputRef.current?.click();
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isUploading) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    if (!isUploading) {
      await saveFontFilesImmediately(event.dataTransfer.files);
    }
  };

  const handleAddFont = () => {
    if (isAddFontDisabled) return;
    onClose();
  };

  const handleCancel = () => {
    if (isUploading) return;
    onClose();
  };

  const handleDeleteRequest = (record) => {
    if (isUploading || !record?.id) return;
    setFontIdPendingDelete(record.id);
    setFontLabelPendingDelete(getCustomFontFileNameWithExtension(record));
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmNo = () => {
    if (isDeletingFont) return;
    setIsDeleteConfirmOpen(false);
    setFontIdPendingDelete(null);
    setFontLabelPendingDelete("");
  };

  const handleDeleteConfirmYes = async () => {
    if (fontIdPendingDelete == null || isDeletingFont) return;

    setIsDeletingFont(true);
    try {
      const deleted = await onDeleteSavedFontRecord?.(fontIdPendingDelete);
      if (deleted) {
        setIsDeleteConfirmOpen(false);
        setFontIdPendingDelete(null);
        setFontLabelPendingDelete("");
      }
    } finally {
      setIsDeletingFont(false);
    }
  };

  const handleRetryFailedUpload = async (pending) => {
    pendingReplaceRecordRef.current = {
      pendingId: pending.id,
      isFailedUpload: true,
    };
    replaceFileInputRef.current?.click();
  };

  const handleDeleteFailedUpload = (pendingId) => {
    setPendingUploads((prev) => prev.filter((row) => row.id !== pendingId));
  };

  const hasFailedUploads = useMemo(
    () => pendingUploads.some((item) => item.status === "failed"),
    [pendingUploads],
  );

  const isFontListEmpty =
    savedCustomFontRecords.length === 0 && pendingUploads.length === 0;

  const isAddFontDisabled =
    isUploading ||
    hasUnresolvedDuplicate ||
    hasFailedUploads ||
    isFontListEmpty;

  const addFontDisabledTitle = isUploading
    ? "Wait for upload to finish"
    : hasFailedUploads
      ? "Remove or replace failed uploads before continuing"
      : hasUnresolvedDuplicate
        ? "Replace or delete the duplicate font before continuing"
        : isFontListEmpty
          ? "Upload a font to continue"
          : undefined;

  const uploadFile = async (file, pendingId, options) => {
    let current = 0;
    let waitTimer = null;
    let fallbackTimer = null;
    let backendDone = false;

    const startWaitSimulation = () => {
      if (waitTimer) return;
      waitTimer = setInterval(() => {
        current = Math.min(current + 0.5, 90);
        updatePendingProgress(
          [pendingId],
          Math.round(current),
          getProgressText(Math.round(current)),
        );
        if (current >= 90) clearInterval(waitTimer);
      }, 300);
    };

    fallbackTimer = setInterval(() => {
      if (current >= 85) {
        clearInterval(fallbackTimer);
        if (backendDone) startWaitSimulation();
        return;
      }
      const increment = current < 50 ? 5 : current < 75 ? 2 : 1;
      current = Math.min(current + increment, 85);
      updatePendingProgress(
        [pendingId],
        Math.round(current),
        getProgressText(Math.round(current)),
      );
    }, 300);

    try {
      const response = await uploadCustomFontsToApi([file], {
        assetInfo,
        replaceFontId: options.replaceFontId,
        onUploadProgress: (progress) => {
          if (progress >= 100) {
            backendDone = true;
            if (current >= 85) startWaitSimulation();
            return;
          }
          const real = Math.min(progress * 0.85, 85);
          if (real > current) {
            current = real;
            updatePendingProgress(
              [pendingId],
              Math.round(current),
              getProgressText(Math.round(current)),
            );
          }
        },
      });

      clearInterval(fallbackTimer);
      clearInterval(waitTimer);
      updatePendingProgress([pendingId], 100, `100%`);

      if (response.success) {
        await refreshFontsList();
        updatePendingProgress([pendingId], 100, `100%`);
        setPendingUploads((prev) => prev.filter((row) => row.id !== pendingId));
      } else {
        setPendingUploads((prev) =>
          prev.map((row) =>
            row.id === pendingId
              ? { ...row, status: "failed", progress: 0 }
              : row,
          ),
        );
      }

      return response.success;
    } catch {
      clearInterval(fallbackTimer);
      clearInterval(waitTimer);
      setPendingUploads((prev) =>
        prev.map((row) =>
          row.id === pendingId
            ? { ...row, status: "failed", progress: 0 }
            : row,
        ),
      );
      return false;
    }
  };

  return (
    <>
      <GenericModal
        open={open && !isDeleteConfirmOpen}
        onClose={handleCancel}
        disableBackdropClose
        disableEscapeKeyClose
        className="custom-fonts-upload-modal"
        width={layout.modalWidth}
        height={layout.modalHeight}
        minWidth={layout.modalMinWidth}
        maxWidth={layout.modalMaxWidth}
        minHeight={layout.modalMinHeight}
        maxHeight={layout.modalMaxHeight}
        title="UPLOAD FONTS"
        footer={
          <div className="custom-fonts-footer">
            <Button
              onClick={handleCancel}
              disabled={isUploading}
              className="footer-plain-btn"
            >
              CLOSE
            </Button>
            {/* <Button
              onClick={handleAddFont}
              className="Footer-btns"
              title={addFontDisabledTitle}
              disabled={isAddFontDisabled}
            >
              ADD FONT
            </Button> */}
          </div>
        }
      >
        <div className="custom-fonts-modal-body" style={modalBodyStyle}>
          <div
            role="button"
            tabIndex={0}
            className={`custom-fonts-dropzone${isDragOver ? " is-dragover" : ""}${
              isUploading ? " is-disabled" : ""
            }`}
            onClick={openFilePicker}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openFilePicker();
              }
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isUploading ? (
              <img
                src={UploadIcon}
                alt="upload"
                className="custom-fonts-dropzone-icon"
                style={{ width: "20px", height: "20px", opacity: 0.5 }}
              />
            ) : (
              <img
                src={UploadIcon}
                alt="upload"
                className="custom-fonts-dropzone-icon"
                style={{ width: "20px", height: "20px" }}
              />
            )}
            <p className="custom-fonts-dropzone-text">
              Drag & drop font or{" "}
              <span style={{ color: "#203662", fontWeight: "700" }}>
                Select to Browse
              </span>
            </p>
            <p className="custom-fonts-dropzone-hint">
              (Max 20 MB) | Supported: {CUSTOM_FONT_SUPPORTED_FORMATS_LABEL}
            </p>
          </div>

          <input
            ref={hiddenFileInputRef}
            type="file"
            multiple
            accept=".ttf,.otf,font/ttf,font/otf"
            style={{ display: "none" }}
            onChange={handleHiddenInputChange}
            disabled={isUploading}
          />
          <input
            ref={replaceFileInputRef}
            type="file"
            accept=".ttf,.otf,font/ttf,font/otf"
            style={{ display: "none" }}
            onChange={handleReplaceInputChange}
            disabled={isUploading}
          />

          <div className="custom-fonts-list-section">
            <UploadedFontsList
              fontRecords={savedCustomFontRecords}
              pendingUploads={pendingUploads}
              onDeleteFontRecord={handleDeleteRequest}
              onReplaceFontRecord={handleReplaceRequest}
              onRetryFailedUpload={handleRetryFailedUpload}
              onDeleteFailedUpload={handleDeleteFailedUpload}
              disabled={isUploading}
            />
          </div>
        </div>
      </GenericModal>

      <CustomDeleteFontsModal
        open={open && isDeleteConfirmOpen}
        fontLabel={fontLabelPendingDelete}
        isDeleting={isDeletingFont}
        onYes={handleDeleteConfirmYes}
        onNo={handleDeleteConfirmNo}
      />
    </>
  );
}
